import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Star, ThumbsUp, MessageSquare, Award } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Star Rating Component
const StarRating = ({ rating, onRatingChange, interactive = false, size = "w-5 h-5" }) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onRatingChange(star)}
          onMouseEnter={() => interactive && setHoverRating(star)}
          onMouseLeave={() => interactive && setHoverRating(0)}
          className={`${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}`}
        >
          <Star
            className={`${size} ${
              star <= (hoverRating || rating)
                ? 'fill-[#E8A007] text-[#E8A007]'
                : 'text-slate-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Rating Distribution Bar
const RatingBar = ({ stars, count, total }) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-3 text-slate-600">{stars}</span>
      <Star className="w-3 h-3 fill-[#E8A007] text-[#E8A007]" />
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#E8A007] rounded-full transition-all"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="w-6 text-slate-500 text-xs">{count}</span>
    </div>
  );
};

// Single Review Card
const ReviewCard = ({ review, onHelpful }) => {
  const [helpfulClicked, setHelpfulClicked] = useState(false);
  
  const handleHelpful = async () => {
    if (helpfulClicked) return;
    try {
      await axios.post(`${API}/reviews/${review.id}/helpful`);
      setHelpfulClicked(true);
      onHelpful(review.id);
      toast.success('Grazie per il feedback!');
    } catch (error) {
      console.error('Error marking helpful:', error);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="border-b border-slate-100 pb-4 last:border-0"
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-[#344B71]">{review.reviewer_name}</span>
            {review.reviewer_company && (
              <span className="text-sm text-slate-500">• {review.reviewer_company}</span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <StarRating rating={review.rating} size="w-4 h-4" />
            <span className="text-xs text-slate-400">
              {new Date(review.created_at).toLocaleDateString('it-IT', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </span>
          </div>
        </div>
        {review.collaboration_type && (
          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
            {review.collaboration_type}
          </span>
        )}
      </div>
      
      <h4 className="font-medium text-slate-800 mt-3">{review.title}</h4>
      <p className="text-slate-600 text-sm mt-1 leading-relaxed">{review.comment}</p>
      
      <button
        onClick={handleHelpful}
        disabled={helpfulClicked}
        className={`flex items-center gap-1 mt-3 text-xs ${
          helpfulClicked ? 'text-[#344B71]' : 'text-slate-400 hover:text-[#344B71]'
        } transition-colors`}
      >
        <ThumbsUp className="w-3 h-3" />
        <span>Utile ({review.helpful_count + (helpfulClicked ? 1 : 0)})</span>
      </button>
    </motion.div>
  );
};

// Main Review Section Component
const ReviewSection = ({ professionalId, professionalName, canReview = false }) => {
  const { user } = useContext(AuthContext);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average_rating: 0, total_reviews: 0, rating_distribution: {} });
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [newReview, setNewReview] = useState({
    rating: 0,
    title: '',
    comment: '',
    collaboration_type: '',
    collaboration_confirmed: false
  });

  useEffect(() => {
    fetchReviews();
    fetchStats();
  }, [professionalId]);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews/professional/${professionalId}`);
      setReviews(res.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/reviews/stats/${professionalId}`);
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSubmitReview = async () => {
    if (newReview.rating === 0) {
      toast.error('Seleziona una valutazione');
      return;
    }
    if (!newReview.title.trim() || !newReview.comment.trim()) {
      toast.error('Compila tutti i campi');
      return;
    }
    if (!newReview.collaboration_confirmed) {
      toast.error('Devi confermare che è intercorso un rapporto di collaborazione');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/reviews`, {
        professional_id: professionalId,
        ...newReview
      });
      
      setReviews([res.data, ...reviews]);
      setShowReviewForm(false);
      setNewReview({ rating: 0, title: '', comment: '', collaboration_type: '', collaboration_confirmed: false });
      fetchStats();
      toast.success('Recensione pubblicata!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'invio');
    } finally {
      setSubmitting(false);
    }
  };

  const handleHelpful = (reviewId) => {
    setReviews(reviews.map(r => 
      r.id === reviewId ? { ...r, helpful_count: r.helpful_count + 1 } : r
    ));
  };

  const collaborationTypes = [
    { value: 'consulting', label: 'Consulenza' },
    { value: 'project', label: 'Progetto' },
    { value: 'audit', label: 'Audit' },
    { value: 'training', label: 'Formazione' },
    { value: 'other', label: 'Altro' }
  ];

  return (
    <Card className="p-6 bg-white mt-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Award className="w-6 h-6 text-[#E8A007]" />
          <h3 className="text-lg font-semibold text-[#344B71]">Recensioni</h3>
        </div>
        
        {canReview && user?.user_type === 'company' && (
          <Dialog open={showReviewForm} onOpenChange={setShowReviewForm}>
            <DialogTrigger asChild>
              <Button className="bg-[#344B71] hover:bg-[#344B71] text-white">
                <MessageSquare className="w-4 h-4 mr-2" />
                Scrivi Recensione
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-[#344B71]">Recensisci {professionalName}</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-slate-700">La tua valutazione</Label>
                  <div className="mt-2">
                    <StarRating
                      rating={newReview.rating}
                      onRatingChange={(r) => setNewReview({ ...newReview, rating: r })}
                      interactive
                      size="w-8 h-8"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-700">Tipo di collaborazione</Label>
                  <Select
                    value={newReview.collaboration_type}
                    onValueChange={(v) => setNewReview({ ...newReview, collaboration_type: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Seleziona..." />
                    </SelectTrigger>
                    <SelectContent>
                      {collaborationTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-slate-700">Titolo</Label>
                  <Input
                    value={newReview.title}
                    onChange={(e) => setNewReview({ ...newReview, title: e.target.value })}
                    placeholder="Riassumi la tua esperienza"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-700">La tua recensione</Label>
                  <Textarea
                    value={newReview.comment}
                    onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                    placeholder="Descrivi la tua esperienza con questo professionista..."
                    rows={4}
                    className="mt-1"
                  />
                </div>
                
                {/* Conferma Responsabilità - OBBLIGATORIA */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newReview.collaboration_confirmed}
                      onChange={(e) => setNewReview({ ...newReview, collaboration_confirmed: e.target.checked })}
                      className="mt-1 w-5 h-5 rounded border-amber-300 text-[#344B71] focus:ring-[#344B71]"
                    />
                    <span className="text-sm text-amber-800">
                      <strong>Dichiaro sotto la mia responsabilità</strong> che tra la mia azienda e questo professionista 
                      è intercorso un rapporto di collaborazione/consulenza (continuativo o spot) e che questa recensione 
                      riflette la mia esperienza reale.
                    </span>
                  </label>
                </div>
                
                <Button
                  onClick={handleSubmitReview}
                  disabled={submitting || !newReview.collaboration_confirmed}
                  className="w-full bg-[#344B71] hover:bg-[#344B71] text-white disabled:opacity-50"
                >
                  {submitting ? 'Invio in corso...' : 'Pubblica Recensione'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats Summary */}
      <div className="flex flex-col md:flex-row gap-6 mb-6 p-4 bg-slate-50 rounded-xl">
        <div className="flex items-center gap-4">
          <div className="text-center">
            <div className="text-4xl font-bold text-[#344B71]">
              {stats.average_rating > 0 ? stats.average_rating.toFixed(1) : '-'}
            </div>
            <StarRating rating={Math.round(stats.average_rating)} size="w-4 h-4" />
            <div className="text-sm text-slate-500 mt-1">
              {stats.total_reviews} {stats.total_reviews === 1 ? 'recensione' : 'recensioni'}
            </div>
          </div>
        </div>
        
        <div className="flex-1 space-y-1">
          {[5, 4, 3, 2, 1].map(stars => (
            <RatingBar
              key={stars}
              stars={stars}
              count={stats.rating_distribution[stars] || 0}
              total={stats.total_reviews}
            />
          ))}
        </div>
      </div>

      {/* Reviews List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#344B71] mx-auto"></div>
        </div>
      ) : reviews.length > 0 ? (
        <div className="space-y-4">
          {reviews.map(review => (
            <ReviewCard key={review.id} review={review} onHelpful={handleHelpful} />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-slate-500">
          <Star className="w-12 h-12 text-slate-200 mx-auto mb-2" />
          <p>Nessuna recensione ancora</p>
          {canReview && user?.user_type === 'company' && (
            <p className="text-sm mt-1">Sii il primo a recensire questo professionista!</p>
          )}
        </div>
      )}
    </Card>
  );
};

export default ReviewSection;
